<div class="vouchers-container">
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Vouchers</h1>
        <p>Manage and view all voucher information</p>
      </div>
    </div>
  </div>

  <!-- Filter Section -->
  <div class="filter-section">
    <div class="filter-header">
      <h3><i class="fas fa-filter"></i> Filter Vouchers</h3>
      <button class="clear-filters-btn" (click)="clearFilters()" [disabled]="!hasActiveFilters()">
        <i class="fas fa-times"></i> Clear Filters
      </button>
    </div>
    
    <div class="filter-form">
      <div class="filter-row">
        <div class="filter-group">
          <label for="phoneFilter">Phone Number</label>
          <input 
            type="text" 
            id="phoneFilter"
            [(ngModel)]="filters.phoneNumber" 
            placeholder="Search by phone number..."
            (input)="onFilterChange()"
            class="filter-input">
        </div>
        
        <div class="filter-group">
          <label for="customerNameFilter">Customer Name</label>
          <input 
            type="text" 
            id="customerNameFilter"
            [(ngModel)]="filters.customerName" 
            placeholder="Search by customer name..."
            (input)="onFilterChange()"
            class="filter-input">
        </div>
        
        <div class="filter-group">
          <label for="partnerFilter">Partner Organisation</label>
          <input 
            type="text" 
            id="partnerFilter"
            [(ngModel)]="filters.partnerName" 
            placeholder="Search by partner organisation..."
            (input)="onFilterChange()"
            class="filter-input">
        </div>
      </div>
      
      <div class="filter-row">
        <div class="filter-group">
          <label for="minAmountFilter">Min Voucher Amount (₹)</label>
          <input 
            type="number" 
            id="minAmountFilter"
            [(ngModel)]="filters.minVoucherAmount" 
            placeholder="Minimum amount..."
            (input)="onFilterChange()"
            class="filter-input"
            step="0.01"
            min="0">
        </div>
        
        <div class="filter-group">
          <label for="maxAmountFilter">Max Voucher Amount (₹)</label>
          <input 
            type="number" 
            id="maxAmountFilter"
            [(ngModel)]="filters.maxVoucherAmount" 
            placeholder="Maximum amount..."
            (input)="onFilterChange()"
            class="filter-input"
            step="0.01"
            min="0">
        </div>
        
        <div class="filter-actions">
          <button class="apply-filters-btn" (click)="applyFilters()" [disabled]="!hasActiveFilters()">
            <i class="fas fa-search"></i> Apply Filters
          </button>
        </div>
      </div>
      
      <div class="filter-row">
        <div class="filter-group">
          <label for="outletIdFilter">Outlet</label>
          <select 
            id="outletIdFilter"
            [(ngModel)]="filters.outletId" 
            (change)="onFilterChange()"
            class="filter-input">
            <option [ngValue]="null">All Outlets</option>
            <option *ngFor="let outlet of outlets" [ngValue]="outlet.id">
              {{ outlet.name || outlet.id }}
            </option>
          </select>
        </div>
        
        <div class="filter-group">
          <label for="statusFilter">Status</label>
          <select 
            id="statusFilter"
            [(ngModel)]="statusFilter" 
            (change)="onFilterChange()"
            class="filter-input">
            <option [ngValue]="null">All Statuses</option>
            <option [ngValue]="VoucherStatus.Active">Active</option>
            <option [ngValue]="VoucherStatus.Redeemed">Redeemed</option>
            <option [ngValue]="VoucherStatus.Expired">Expired</option>
            <option [ngValue]="VoucherStatus.Cancelled">Cancelled</option>
          </select>
        </div>
      </div>
      
      <div class="filter-row">
        <div class="filter-group">
          <label for="redemptionReferenceFilter">Redemption Reference</label>
          <input 
            type="text" 
            id="redemptionReferenceFilter"
            [(ngModel)]="filters.redemptionReference" 
            placeholder="Search by redemption reference..."
            (input)="onFilterChange()"
            class="filter-input">
        </div>
        
        <div class="filter-group">
          <label for="fromDateFilter">From Date</label>
          <input 
            type="date" 
            id="fromDateFilter"
            [(ngModel)]="filters.fromDate" 
            (change)="onFilterChange()"
            class="filter-input">
        </div>
        
        <div class="filter-group">
          <label for="toDateFilter">To Date</label>
          <input 
            type="date" 
            id="toDateFilter"
            [(ngModel)]="filters.toDate" 
            (change)="onFilterChange()"
            class="filter-input">
        </div>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage" class="error-message">
    <div class="error-content">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ errorMessage }}</span>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading">
    <p>Loading vouchers...</p>
  </div>

  <!-- Table Container -->
  <div class="table-container" *ngIf="!loading && vouchers.length > 0">
    <!-- Pagination Summary -->
    <div class="pagination-summary">
      <p>Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount || 0) }} of {{ totalCount || 0 }} vouchers</p>
    </div>
    
    <!-- Desktop Table View -->
    <div class="table-wrapper">
      <table class="vouchers-table">
        <thead>
          <tr>
            <th>Voucher Code</th>
            <th>Customer Name</th>
            <th>Phone Number</th>
            <th>Voucher Amount</th>
            <th>Partner Organisation</th>
            <th>Expiry Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let voucher of vouchers">
            <!-- Main row -->
            <tr class="voucher-row" [class.expanded]="isRowExpanded(voucher.id)">
              <td><strong>{{ voucher.voucherCode }}</strong></td>
              <td>{{ voucher.customerName }}</td>
              <td>{{ voucher.customerPhoneNumber }}</td>
              <td>{{ formatCurrency(voucher.voucherAmount) }}</td>
              <td>{{ voucher.partnerName }}</td>
              <td>{{ formatDate(voucher.expiryDate) }}</td>
              <td>
                <span class="status-badge" [ngClass]="getStatusClass(voucher.status)">
                  {{ getStatusText(voucher.status) }}
                </span>
              </td>
              <td>
                <div class="action-buttons">
                  <button 
                    class="expand-btn" 
                    (click)="toggleRow(voucher.id)"
                    [class.expanded]="isRowExpanded(voucher.id)" 
                    title="Expand to see details">
                    {{ isRowExpanded(voucher.id) ? '▼' : '▶' }}
                  </button>
                </div>
              </td>
            </tr>
            
            <!-- Expanded row with full details -->
            <tr class="details-row" *ngIf="isRowExpanded(voucher.id)">
              <td colspan="8">
                <div class="voucher-details">
                  <div class="details-header">
                    <h4>Voucher Details</h4>
                  </div>
                  <div class="details-grid">
                    <div class="detail-item">
                      <label>Voucher ID:</label>
                      <span>{{ voucher.id }}</span>
                    </div>
                    <div class="detail-item" *ngIf="voucher.voucherConfigName">
                      <label>Voucher Config Name:</label>
                      <span>{{ voucher.voucherConfigName }}</span>
                    </div>
                    <div class="detail-item">
                      <label>Voucher Amount:</label>
                      <span class="highlight">{{ formatCurrency(voucher.voucherAmount) }}</span>
                    </div>
                    <div class="detail-item">
                      <label>Minimum Purchase Amount:</label>
                      <span>{{ formatCurrency(voucher.minimumPurchaseAmount) }}</span>
                    </div>
                    <div class="detail-item">
                      <label>Partner Name:</label>
                      <span>{{ voucher.partnerName }}</span>
                    </div>
                    <div class="detail-item" *ngIf="voucher.partnerLogo">
                      <label>Partner Logo:</label>
                      <span>{{ voucher.partnerLogo }}</span>
                    </div>
                    <div class="detail-item" *ngIf="voucher.outletId">
                      <label>Outlet ID:</label>
                      <span>{{ voucher.outletId }}</span>
                    </div>
                    <div class="detail-item" *ngIf="voucher.outletName">
                      <label>Outlet Name:</label>
                      <span>{{ voucher.outletName }}</span>
                    </div>
                    <div class="detail-item" *ngIf="voucher.partnerUserId">
                      <label>Partner User ID:</label>
                      <span>{{ voucher.partnerUserId }}</span>
                    </div>
                    <div class="detail-item" *ngIf="voucher.partnerUserName">
                      <label>Partner User Name:</label>
                      <span>{{ voucher.partnerUserName }}</span>
                    </div>
                    <div class="detail-item">
                      <label>Expiry Date:</label>
                      <span>{{ formatDate(voucher.expiryDate) }}</span>
                    </div>
                    <div class="detail-item">
                      <label>Status:</label>
                      <span class="status-badge" [ngClass]="getStatusClass(voucher.status)">
                        {{ getStatusText(voucher.status) }}
                      </span>
                    </div>
                    <div class="detail-item" *ngIf="voucher.redeemedAt">
                      <label>Redeemed At:</label>
                      <span>{{ formatDate(voucher.redeemedAt) }}</span>
                    </div>
                    <div class="detail-item" *ngIf="voucher.redemptionReference">
                      <label>Redemption Reference:</label>
                      <span>{{ voucher.redemptionReference }}</span>
                    </div>
                    <div class="detail-item full-width" *ngIf="voucher.notes">
                      <label>Notes:</label>
                      <span>{{ voucher.notes }}</span>
                    </div>
                    <div class="detail-item full-width" *ngIf="voucher.itemContributions && voucher.itemContributions.length > 0">
                      <label>Item Contributions:</label>
                      <div class="item-contributions">
                        <table class="contributions-table">
                          <thead>
                            <tr>
                              <th>Scrap Item</th>
                              <th>Multiplier</th>
                              <th>Contribution Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr *ngFor="let contribution of voucher.itemContributions">
                              <td>{{ contribution.scrapItemName }}</td>
                              <td>{{ contribution.multiplier }}x</td>
                              <td>{{ formatCurrency(contribution.contributionAmount) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </div>
  </div>

  <!-- No Data Message -->
  <div *ngIf="!loading && vouchers.length === 0" class="no-data">
    <p>No vouchers found.</p>
  </div>

  <!-- Pagination Component -->
  <app-pagination
    *ngIf="!loading && vouchers.length > 0"
    [page]="currentPage"
    [totalPages]="totalPages"
    [pageSize]="pageSize"
    (pageChange)="onPageChange($event)"
    (pageSizeChange)="onPageSizeChange($event)">
  </app-pagination>
</div>
